import { Access } from "@p0security/shared/types/access/index";

import { CLICKHOUSE_CONSOLE_ROLES } from "./console-roles.ts";
import { CLICKHOUSE_LEVELS } from "./levels.ts";

// Every access grants to the requestor's own ClickHouse Cloud member, found by
// their P0 email, on one service. P0 creates one custom role per request, with
// the member as its only actor, and deletes it when the access ends.

// The requestor picks it from the services of every installed organization,
// by name or ID. The accesses have no organization parameter: `normalize`
// finds the service's organization and records it below.
const service = {
  description: "The ClickHouse Cloud service, by name or ID",
  label: "Service",
  type: "dynamic",
} as const;

// `normalize` sets these from the service the requestor chose, so the
// approver sees which organization and service the request is for, and the
// lifecycle acts on IDs.
const organizationId = {
  description: "The ID of the service's ClickHouse Cloud organization",
  label: "Organization ID",
  type: "generated",
} as const;

const organizationName = {
  description: "The name of the service's ClickHouse Cloud organization",
  label: "Organization",
  type: "generated",
} as const;

const serviceId = {
  description: "The ID of the service",
  label: "Service ID",
  type: "generated",
} as const;

const serviceName = {
  description: "The name of the service",
  label: "Service name",
  type: "generated",
} as const;

/** What a `sql` request's object is. The whole service is `*`. */
export const CLICKHOUSE_OBJECT_KINDS = [
  "service",
  "database",
  "table",
  "view",
  "materialized-view",
  "dictionary",
] as const;
export type ClickHouseObjectKind = (typeof CLICKHOUSE_OBJECT_KINDS)[number];

// The custom role P0 creates for the request. The lifecycle names it, so the
// approver can find it in the Cloud console's activity log.
const roleName = {
  description:
    "The ClickHouse Cloud custom role P0 created for this request, deleted when the access ends",
  label: "Cloud role",
  type: "string",
} as const;

export const ClickHouseAccesses = {
  sql: Access({
    delegation: {},
    description:
      "A read, write or admin level on a whole service, a database, a table, a view or a dictionary, in SQL console",
    generated: { roleName },
    label: "SQL access",
    maturity: "beta",
    // Reads `sql <service> <object> <level>`.
    parameters: {
      service,
      object: {
        description:
          "'*' (quoted in the CLI) for the whole service, a database, or a table, view or dictionary as database.name",
        label: "Object",
        type: "dynamic",
      },
      level: {
        choices: CLICKHOUSE_LEVELS,
        description:
          "read grants SELECT and dictGet. write adds INSERT, ALTER UPDATE, ALTER DELETE and TRUNCATE. admin adds CREATE, DROP, ALTER and OPTIMIZE.",
        label: "Level",
        type: "static",
      },
      organizationId,
      organizationName,
      serviceId,
      serviceName,
      objectKind: {
        choices: CLICKHOUSE_OBJECT_KINDS,
        description: "What the object is",
        label: "Object kind",
        type: "generated",
      },
    },
    render: {
      locator: "serviceName",
      policy: "level",
      resource: "object",
    } as const,
  }),
  console: Access({
    delegation: {},
    description:
      "SQL console read-only or admin, or the Service Reader or Service Admin permissions, on one service",
    generated: { roleName },
    label: "Console role",
    maturity: "beta",
    // Reads `console <service> <console-role>`.
    parameters: {
      service,
      consoleRole: {
        choices: CLICKHOUSE_CONSOLE_ROLES,
        description:
          "sql-console-readonly or sql-console-admin for SQL console, or service-reader or service-admin for the permissions of ClickHouse Cloud's Service Reader or Service Admin role",
        label: "Console role",
        type: "static",
      },
      organizationId,
      organizationName,
      serviceId,
      serviceName,
    },
    render: { locator: "serviceName", policy: "consoleRole" } as const,
  }),
};

export type ClickHouseAccesses = typeof ClickHouseAccesses;
export type ClickHouseSqlAccess = ClickHouseAccesses["sql"];
export type ClickHouseConsoleAccess = ClickHouseAccesses["console"];
